import { Box, Stack, Typography } from '@mui/material';

/**
 * Background image for the left branding panel.
 * Optimized WebP (2000px) for desktop usage.
 */
const BG_URL = `${process.env.PUBLIC_URL}/assets/esimsta-auth-bg.webp`;

/**
 * Layout constants
 * - RIGHT_OFFSET: Controls horizontal positioning of the text block
 *                 (larger value moves content further left)
 * - SAFE_PADDING: Minimum left-side padding to prevent overflow on narrow screens
 */
const RIGHT_OFFSET = 60;
const SAFE_PADDING = 150;

/**
 * Color system for text hierarchy on dark background
 * - Brand: pure white for maximum emphasis
 * - Primary: main descriptive text
 * - Secondary: supporting description
 * - Muted: micro-copy / caption
 */
const COLORS = {
  BRAND: '#FFFFFF',
  PRIMARY: 'rgba(255,255,255,0.86)',
  SECONDARY: 'rgba(255,255,255,0.80)',
  MUTED: 'rgba(255,255,255,0.68)',
};

const Introduction = () => {
  return (
    <Box
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',

        // Background image (no overlay by design)
        backgroundImage: `url(${BG_URL})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',

        // Slight horizontal shift to keep the globe visible on large screens
        backgroundPosition: { md: '8% center', lg: '10% center', xl: '12% center' },
      }}
    >
      {/* Text container */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',

          // Right-based positioning (left remains auto)
          right: RIGHT_OFFSET,

          /**
           * Responsive width rule:
           * - Min width: 360px (readability)
           * - Max width: 700px (prevents overly long lines)
           * - Shrinks automatically to stay within viewport:
           *   width <= 100% - right offset - safe padding
           */
          width: `clamp(
            360px,
            calc(100% - ${RIGHT_OFFSET}px - ${SAFE_PADDING}px),
            700px
          )`,
        }}
      >
        <Stack spacing={3.5}>
          {/* Brand & headline */}
          <Box>
            <Typography
              variant="h3"
              fontWeight={850}
              letterSpacing={-0.8}
              sx={{
                lineHeight: 1.05,
                color: COLORS.BRAND,
                textShadow:
                  '0 2px 10px rgba(0,0,0,0.55), 0 10px 30px rgba(0,0,0,0.35)',
              }}
            >
              eSIM Station
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                mt: 1.1,
                color: COLORS.PRIMARY,
                textShadow: '0 2px 10px rgba(0,0,0,0.45)',
              }}
            >
              Wholesale eSIM distribution across global and local operators.
            </Typography>

            {/* Subtle divider for visual structure */}
            <Box
              sx={{
                mt: 2.5,
                width: 56,
                height: 2,
                borderRadius: 999,
                bgcolor: COLORS.BRAND,
                opacity: 0.55,
                boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
              }}
            />
          </Box>

          {/* Value proposition */}
          <Stack spacing={1.5}>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.7,
                color: COLORS.PRIMARY,
                textShadow: '0 2px 12px rgba(0,0,0,0.45)',
              }}
            >
              Source and distribute eSIM products from multiple mobile network
              operators and service providers worldwide.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.7,
                color: COLORS.SECONDARY,
                textShadow: '0 2px 12px rgba(0,0,0,0.45)',
              }}
            >
              Offer both global roaming eSIMs and country-specific local plans,
              aligned with your markets and business models.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.7,
                color: COLORS.SECONDARY,
                textShadow: '0 2px 12px rgba(0,0,0,0.45)',
              }}
            >
              Manage products, orders, and operations through a unified,
              role-based console.
            </Typography>
          </Stack>

          {/* Micro copy */}
          <Typography
            variant="caption"
            sx={{
              color: COLORS.MUTED,
              textShadow: '0 2px 10px rgba(0,0,0,0.45)',
            }}
          >
            Wholesale • Multi-operator • Partner-focused eSIM operations
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default Introduction;
